import { apiClient } from './client';
// ORB-290: shared with the Unlayer image upload callback in UnlayerDesigner.
// Must match the server's IImageUploadService limits exactly — the client check
// is a UX optimization so we don't post a doomed multipart payload; the server
// is still authoritative.
export const MAX_EMAIL_IMAGE_BYTES = 10 * 1024 * 1024;
export const ALLOWED_EMAIL_IMAGE_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
];
export const assetApi = {
    /**
     * Returns the field-length + requirement catalog for the given asset type
     * (e.g. "ThankYou", "Web", "OnlineEvent", "Download"). Cached server-side
     * for 10 minutes — safe to call on mount per page.
     */
    getFieldLimits: (assetType) => apiClient.get(`/api/Assets/FieldLimits/${encodeURIComponent(assetType)}`),
    getWebAssetsByWeb: async (clientCompanyId, workspaceId, campaignId, webId) => {
        return apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets`);
    },
    createWebAsset: async (clientCompanyId, workspaceId, campaignId, webId, data, file) => {
        const formData = new FormData();
        formData.append('data', JSON.stringify(data));
        formData.append('file', file);
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets`, formData);
    },
    createWebAssetWithoutFile: async (clientCompanyId, workspaceId, campaignId, webId, data) => {
        // Email types are created without a file — Webster generates the content.
        // Send as multipart form data (matching the endpoint's parser) with data only.
        const formData = new FormData();
        formData.append('data', JSON.stringify(data));
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets`, formData);
    },
    createWebAssetByCloudStorage: async (clientCompanyId, workspaceId, campaignId, webId, data) => {
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets`, data);
    },
    getAssetDetail: async (clientCompanyId, workspaceId, campaignId, assetId) => {
        return apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}`);
    },
    getAssetVersions: async (clientCompanyId, workspaceId, assetId) => {
        return apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Assets/${assetId}/Versions`);
    },
    getAssetComments: async (clientCompanyId, workspaceId, campaignId, assetId) => {
        return apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/Comments`);
    },
    addComment: async (clientCompanyId, workspaceId, campaignId, assetId, dto) => {
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/Comments`, dto);
    },
    signOff: async (clientCompanyId, workspaceId, campaignId, assetId, dto) => {
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/SignOff`, dto);
    },
    updateAsset: async (clientCompanyId, workspaceId, campaignId, assetId, dto) => {
        return apiClient.put(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}`, dto);
    },
    uploadVersion: async (clientCompanyId, workspaceId, assetId, data, file) => {
        const formData = new FormData();
        formData.append('data', JSON.stringify(data));
        formData.append('file', file);
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Assets/${assetId}/Versions`, formData);
    },
    uploadVersionByCloudStorage: async (clientCompanyId, workspaceId, assetId, data) => {
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Assets/${assetId}/Versions`, data);
    },
    getVersionStatus: async (clientCompanyId, workspaceId, assetId, versionId, signal) => {
        return apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Assets/${assetId}/Versions/${versionId}`, { signal });
    },
    deleteAsset: async (clientCompanyId, workspaceId, campaignId, assetId) => {
        return apiClient.delete(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}`);
    },
    promoteDraft: async (clientCompanyId, workspaceId, campaignId, assetId, options) => {
        const body = {};
        if (options?.versionComment)
            body.VersionComment = options.versionComment;
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/PromoteDraft`, body);
    },
    loadVersionIntoDraft: async (clientCompanyId, workspaceId, campaignId, assetId, versionNumber) => {
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/LoadVersionIntoDraft`, { VersionNumber: versionNumber });
    },
    // ORB-243: kicks off bulk email-asset generation. Server creates one Draft
    // WebAsset per missing catalog entry (per WebType), then enqueues session-
    // throttled generation messages. 202 Accepted; per-asset progress arrives
    // via the existing emailasset-{assetId} SignalR group.
    generateAllEmails: async (clientCompanyId, workspaceId, campaignId, webId) => {
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets/GenerateAllEmails`);
    },
    // ORB-249: list Email Template-type assets in a Web for the "bind copy to template" dropdown.
    getEmailTemplatesByWeb: async (clientCompanyId, workspaceId, campaignId, webId) => {
        return apiClient.get(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/EmailTemplates`);
    },
    // ORB-289: render an Email Copy into deliverable HTML for the live preview pane.
    // Copies now own their full Unlayer design — the optional HtmlOverride lets the editor
    // preview unsaved-keystroke edits by sending Unlayer's just-compiled HTML without
    // round-tripping a save. Server runs the Web-entity variable pass; no slot merge.
    previewEmailCopy: async (clientCompanyId, workspaceId, campaignId, assetId, overrides, signal) => {
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/EmailCopyPreview`, overrides ?? {}, { signal });
    },
    // ORB-289: re-clone the currently bound template's DraftDesign onto the copy, discarding
    // the copy's customizations. Server clears DraftHtml so the editor regenerates it on the
    // first post-reset open. Returns the updated copy DTO.
    resetCopyToTemplate: async (clientCompanyId, workspaceId, campaignId, assetId) => {
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/ResetCopyToTemplate`, {});
    },
    // ORB-290: upload an image from the Unlayer editor's native image picker into our
    // public-images blob container (demandwebs storage account). Server returns a stable
    // CDN URL the editor inserts directly into the design. Content-hash naming means
    // re-uploading the same bytes resolves to the same URL within a workspace.
    uploadEmailImage: async (clientCompanyId, workspaceId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Email/Images`, formData);
    },
    // Render an Email Template asset alone (with placeholder body / CTA) for the Asset Review
    // preview pane. Mirrors previewEmailCopy's response shape so the dashboard can reuse the
    // iframe + warning-banner pattern. Server fills in sensible placeholders unless overrides
    // are sent.
    previewEmailTemplate: async (clientCompanyId, workspaceId, campaignId, templateAssetId, overrides, signal) => {
        return apiClient.post(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${templateAssetId}/EmailTemplatePreview`, overrides ?? {}, { signal });
    },
};
